import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { shouldAlwaysShowSuccessfulSubmit } from "../../components/featureFlags";
import UploadForm from "../../components/UploadForm";
import LoadingOverlay from "../../components/LoadingOverlay";
import { CheckInShell, Icon } from "../../components/CheckInDesign";
import { getPreferredIdUploadLanguage } from "../../components/languagePreference";
import {
  buildProblemPageState,
  getLocalizedErrorCopy,
  classifyFetchFailure,
  ERROR_KIND,
  getSubmitErrorMessage,
  isAlreadyUploadedError,
  isNotFoundError,
} from "../../components/userFacingErrors";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

export const ensureObjectFormat = (value) => {
  if (!value) return { Descrizione: "" };
  if (typeof value === "string") return { Descrizione: value };
  return value;
};

export const createEmptyMember = () => ({
  surname: "",
  givenname: "",
  gender: "",
  dateOfBirth: "2024-01-01",
  placeOfBirth: { Descrizione: "" },
  citizenship: { Descrizione: "" },
  documentType: { Descrizione: "" },
  documentNumber: "",
  placeOfReleaseDocument: { Descrizione: "" },
});

const saveUploadContext = (context) => {
  try {
    localStorage.setItem("idupload_ctx", JSON.stringify(context));
  } catch (error) {
    console.warn("[Idupload] could not persist upload context", error);
  }
};

export const normalizeStoredGroupInfo = (savedData) => {
  if (!savedData) return [createEmptyMember()];
  const members = JSON.parse(savedData);
  return members.map((member) => ({
    ...member,
    citizenship: ensureObjectFormat(member.citizenship),
    placeOfBirth: ensureObjectFormat(member.placeOfBirth),
    documentType: ensureObjectFormat(member.documentType),
    placeOfReleaseDocument: ensureObjectFormat(member.placeOfReleaseDocument),
  }));
};

export const hasPlace = (value) =>
  !!(value && (value.Codice || (value.Descrizione && value.Descrizione.trim().length > 0)));

export const getMissingFields = (groupInfo) => {
  const leader = groupInfo[0] || {};
  const missing = [];

  if (!leader.surname) missing.push("Surname");
  if (!leader.givenname) missing.push("Name");
  if (!leader.gender) missing.push("Gender");
  if (!leader.documentNumber) missing.push("Document Number");
  if (!hasPlace(leader.placeOfBirth)) missing.push("Place of Birth");
  if (!hasPlace(leader.citizenship)) missing.push("Citizenship");
  if (!leader.documentType?.Descrizione?.trim()) missing.push("Document Type");
  if (!hasPlace(leader.placeOfReleaseDocument)) missing.push("Place of release document");

  return missing;
};

export default function Idupload() {
  const [idUploaded, setIdUploaded] = useState(false);
  const [groupInfo, setGroupInfo] = useState(() =>
    normalizeStoredGroupInfo(localStorage.getItem("groupInfo"))
  );
  const [reservationInfo, setReservationInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitNotice, setSubmitNotice] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/pagenotfound");
      return;
    }
    fetchReservationInfo();
    if (localStorage.getItem("groupInfo")) setIdUploaded(true);
  }, [id]);

  const submitGeneratedGroupInfo = async (nextGroupInfo) => {
    const groupInfoForSubmit = Array.isArray(nextGroupInfo) ? nextGroupInfo : groupInfo;
    const missing = getMissingFields(groupInfoForSubmit);

    setSubmitNotice("");
    if (missing.length > 0) {
      setSubmitNotice(getLocalizedErrorCopy("missing_fields", getPreferredIdUploadLanguage()).short);
      return;
    }

    setGroupInfo(groupInfoForSubmit);
    setSubmitting(true);
    localStorage.setItem("groupInfo", JSON.stringify(groupInfoForSubmit));

    try {
      const response = await axios.post(
        `${REACT_APP_BASE_URL}/idupload/input/${id}`,
        { groupInfo: groupInfoForSubmit, reservationInfo }
      );
      if (response.data !== "Succeed" && response.data?.accepted !== true && !shouldAlwaysShowSuccessfulSubmit()) {
        setSubmitting(false);
        setSubmitNotice(getSubmitErrorMessage(null, getPreferredIdUploadLanguage()));
        return;
      }
      console.log(response.data);
      localStorage.removeItem("groupInfo");
      setLoading(true);
      setSubmitting(false);
    } catch (err) {
      console.log(err);
      setSubmitting(false);

      if (shouldAlwaysShowSuccessfulSubmit()) {
        localStorage.removeItem("groupInfo");
        setLoading(true);
        return;
      }
      setSubmitNotice(getSubmitErrorMessage(err, getPreferredIdUploadLanguage()));
    }
  };

  const fetchReservationInfo = async () => {
    if (!id) return;
    try {
      const response = await axios.get(`${REACT_APP_BASE_URL}/idupload/fetch/${id}`);
      const payload = response.data;

      if (
        typeof payload !== "object" ||
        payload === null ||
        !payload.reservationInfo
      ) {
        console.error("[Idupload] fetch returned non-JSON or missing reservationInfo", typeof payload);
        return navigate(
          "/service-unavailable",
          buildProblemPageState({
            reason: ERROR_KIND.TEMPORARY,
            retryPath: `/${encodeURIComponent(id)}`,
          })
        );
      }

      setReservationInfo(payload.reservationInfo);
      saveUploadContext({
        confirmationCode: id,
        storedAt: new Date().toISOString(),
        expiresAt: payload.reservationInfo?.checkOut || null,
      });
    } catch (err) {
      const status = err.response?.status;
      const raw = err.response?.data;

      if (isAlreadyUploadedError(status, raw)) {
        saveUploadContext({
          confirmationCode: id,
          storedAt: new Date().toISOString(),
          expiresAt: null,
        });
        return navigate("/uploaded");
      }

      if (isNotFoundError(status, raw)) {
        return navigate("/pagenotfound", {
          state: { reason: "reservation_not_found" },
        });
      }

      console.error("[Idupload] fetchReservation failed", err);
      return navigate(
        "/service-unavailable",
        buildProblemPageState({
          reason: classifyFetchFailure(err),
          retryPath: `/${encodeURIComponent(id)}`,
        })
      );
    }
  };

  return (
    <CheckInShell>
      <LoadingOverlay
        loading={submitting}
        message="Submitting your information. Please wait..."
      />
      <div className="v0-card-scroll">
        {submitNotice && <div className="v0-page-error-banner" role="status" aria-live="polite">
          <p className="v0-page-error-text">{submitNotice}</p>
          <button type="button" onClick={() => submitGeneratedGroupInfo(groupInfo)} disabled={submitting}>{getPreferredIdUploadLanguage() === "it" ? "Riprova" : "Try again"}</button>
        </div>}
        {!loading ? (
          <form className="was-validated v0-review-form" onSubmit={(event) => event.preventDefault()}>
            <UploadForm
              setIdUploaded={setIdUploaded}
              setGroupInfo={setGroupInfo}
              groupInfo={groupInfo}
              id={id}
              reservationInfo={reservationInfo}
              onAutoSubmit={submitGeneratedGroupInfo}
              onValidatedUploadWithoutExtract={() => navigate("/uploaded")}
            />
          </form>
        ) : (
          <div className="v0-card-stack">
            <div className="v0-page-success-banner" role="status" aria-live="polite">
              <span className="v0-page-success-icon" aria-hidden="true">
                <Icon name="check" size={22} />
              </span>
              <div className="v0-page-success-copy">
                <p className="v0-page-success-text">Thank You!</p>
                <p className="v0-page-success-detail">Your information has been sent successfully.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </CheckInShell>
  );
}
